import React from 'react';
import LoadingIndicator from 'src/components/LoadingIndicator';
import NavTab from 'src/components/NavTab';
import SearchBar from 'src/components/SearchBar';
import { ShipmentsList, ShipmentsTable } from 'src/components/Shipments';
import emptyImg from 'src/images/empty.svg';
import {
  Button,
  Card,
  Col,
  Container,
  Icon,
  InputSelect,
  Nav,
  Row,
  ScreenSize,
  useApp,
  useUrlState
} from 'straightline-ui';
import { STATUSES } from 'straightline-utils/constants';

export default function Shipments() {
  const app = useApp();
  const queryState = useUrlState();
  const { getInState, setInState, delInState } = queryState;
  const query = getInState('query');
  const $sort = getInState('query.$sort', { created_at: -1 });
  const status_type = getInState('query.status_type', 'all');

  const {
    result: shipments,
    isLoading,
    isError,
    error,
    isFetching
  } = app
    .service('api/shipments')
    .useFind({ query: { ...query, $sort } }, { keepPreviousData: true });

  const handleTabChange = async (status_type) => {
    if (status_type === 'all') {
      return delInState('query.status_type');
    }

    return setInState('query.status_type', status_type);
  };

  if (isLoading) {
    return (
      <div className="d-flex vh-100 align-items-center">
        <LoadingIndicator delay={250} />
      </div>
    );
  }

  if (isError) {
    return (
      <Container>
        <Row className="align-items-center justify-content-center vh-100">
          <Col lg="6" className="text-center">
            <img
              src={emptyImg}
              alt="Not Found"
              className="img-fluid animated fadeIn faster mb-5"
            />
            <h1>{error.message || 'There was an unexpected error'}</h1>
            <Button to="/dashboard" color="primary" size="lg" className="mr-3">
              <Icon type="home" className="mr-2" />
              Return to Dashboard
            </Button>
            <Button to="/support" color="secondary" size="lg">
              <Icon type="send" className="mr-2" />
              Contact Straightline
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="pb-5">
      <div className="header pt-md-2">
        <div className="header-body">
          <Row className="align-items-center">
            <Col>
              <h6 className="header-pretitle">Overview</h6>
              <h1 className="header-title">Shipments</h1>
            </Col>
            <Col className="col-auto">
              <Button to="/quote" color="primary">
                <Icon type="clipboard" className="mr-2" />
                Request Quote
              </Button>
            </Col>
          </Row>
          <ScreenSize md lg xl>
            {() => (
              <Row
                className={`align-items-center ${
                  isFetching ? 'is-fetching' : ''
                }`}
              >
                <Col>
                  <Nav tabs className="nav-overflow header-tabs">
                    <NavTab
                      tab="all"
                      onClick={handleTabChange}
                      activeTab={status_type}
                    >
                      All Shipments
                    </NavTab>
                    {STATUSES.map((status) => (
                      <NavTab
                        key={status.value}
                        tab={status.value}
                        onClick={handleTabChange}
                        activeTab={status_type}
                      >
                        {status.label}
                      </NavTab>
                    ))}
                  </Nav>
                </Col>
              </Row>
            )}
          </ScreenSize>
          <ScreenSize xs sm>
            {() => (
              <div className="mt-4">
                <InputSelect
                  value={status_type}
                  onChange={({ target }) => handleTabChange(target.value)}
                  options={[{ label: 'All Shipments', value: 'all' }].concat(
                    STATUSES
                  )}
                />
              </div>
            )}
          </ScreenSize>
        </div>
      </div>
      <div className={isFetching ? 'is-fetching' : ''}>
        <ScreenSize lg xl>
          {() => (
            <ShipmentTable
              shipments={shipments.data}
              queryState={queryState}
              total={shipments.total}
            />
          )}
        </ScreenSize>
        <ScreenSize xs sm md>
          {() => (
            <ShipmentCards
              shipments={shipments.data}
              queryState={queryState}
              total={shipments.total}
            />
          )}
        </ScreenSize>
      </div>
    </Container>
  );
}

const sortLabels = {
  pickup_date: 'Pickup Date',
  due_date: 'Delivery Date',
  mode: 'Mode',
  status_type: 'Status'
};

const ShipmentTable = ({ shipments, queryState, total }) => {
  return (
    <Card>
      <SearchBar
        queryState={queryState}
        total={total}
        sortLabels={sortLabels}
      />
      <ShipmentsTable data={shipments} />
    </Card>
  );
};

const ShipmentCards = ({ shipments, queryState, total }) => {
  return (
    <div>
      <Card className="mx-n3">
        <SearchBar
          queryState={queryState}
          total={total}
          sortLabels={sortLabels}
        />
      </Card>
      <Card className="mx-n3">
        <ShipmentsList data={shipments} />
      </Card>
    </div>
  );
};
